import { styled } from "@mui/material/styles";
import {
  Formik,
  type FormikHelpers,
  type FormikValues,
  Form as FormikForm,
} from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import type { ZodTypeAny } from "zod";

type FormShellProps<T extends FormikValues> = {
  children: React.ReactNode;
  initialValues: T;
  validationSchema: ZodTypeAny;
  footer?: React.ReactNode;
  // The one supported reset: a changed resetKey remounts Formik, re-reading
  // initialValues. Needed when the edited entity changes while the form stays
  // mounted (e.g. edit sidebar switching rows) — never hand-roll a key= on
  // the shell. Reopening a closed Sidebar resets on its own: Sidebar unmounts
  // its children after the slide-out.
  resetKey?: React.Key;
  onSubmit: (values: T, helpers: FormikHelpers<T>) => void;
};

const FormShell = <T extends FormikValues>({
  children,
  footer,
  initialValues,
  onSubmit,
  resetKey,
  validationSchema,
}: FormShellProps<T>): React.ReactElement => (
  <Formik<T>
    initialValues={initialValues}
    key={resetKey}
    onSubmit={onSubmit}
    validateOnBlur={false}
    validateOnChange={false}
    validationSchema={toFormikValidationSchema(validationSchema)}
  >
    <StyledForm>
      <FormLayout>{children}</FormLayout>
      {footer}
    </StyledForm>
  </Formik>
);

const StyledForm = styled(FormikForm)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: theme.spacing(2),
}));

const FormLayout = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  flex: 1,
}));

export { FormShell, type FormShellProps };
